import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Stack,
  Box,
  Chip,
  Divider,
  Button,
  Alert,
} from '@mui/joy'

/**
 * Detalle de una cita EXTRAÍDA de Google Calendar.
 *
 * Modal aparte del de citas normales a propósito: una cita de Google no tiene
 * servicio del catálogo, ni agente asignado, ni precio, ni recordatorio, ni
 * historial de mensajes. Reutilizar el modal normal llenaba la pantalla de
 * "N/A" y "$0", que parecen datos rotos cuando en realidad esos conceptos
 * sencillamente no existen para este tipo de cita.
 *
 * Aquí se muestra SOLO lo que el evento de Google realmente contiene.
 */

interface Props {
  open: boolean
  onClose: () => void
  appointment: any | null
}

const formatearFecha = (iso?: string | null): string => {
  if (!iso) return 'Sin fecha'
  try {
    return new Date(iso).toLocaleString('es-EC', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return String(iso)
  }
}

/** Fila etiqueta/valor. `mono` para teléfonos e identificadores. */
const Dato = ({
  etiqueta,
  valor,
  mono,
}: {
  etiqueta: string
  valor: React.ReactNode
  mono?: boolean
}) => (
  <Box>
    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
      {etiqueta}
    </Typography>
    <Typography
      level="body-sm"
      sx={mono ? { fontFamily: 'monospace' } : undefined}
    >
      {valor}
    </Typography>
  </Box>
)

export default function GoogleAppointmentModal({ open, onClose, appointment }: Props) {
  if (!appointment) return null

  const meta = appointment.metadata || {}
  const cliente = appointment.contact?.name || null
  const telCliente = meta.customerPhone || appointment.attendeePhone || null
  const telNegocio = meta.businessPhone || null

  const estados: Record<string, { label: string; color: any }> = {
    scheduled: { label: 'Programada', color: 'warning' },
    confirmed: { label: 'Confirmada', color: 'success' },
    cancelled: { label: 'Cancelada', color: 'danger' },
    completed: { label: 'Completada', color: 'neutral' },
  }
  const estado = estados[appointment.status] || {
    label: appointment.status,
    color: 'neutral',
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: 520, width: '100%' }}>
        <ModalClose />

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography level="title-lg">Cita de Google Calendar</Typography>
        </Stack>
        <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 2 }}>
          Creada por un agente externo. ChatEAM solo la leyó del calendario:
          no tiene servicio, agente ni precio asociados.
        </Typography>

        <Stack spacing={2}>
          {/* Qué y cuándo */}
          <Box>
            <Typography level="title-md">
              {appointment.title || 'Evento sin título'}
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {formatearFecha(appointment.startTime)}
            </Typography>
            <Chip size="sm" color={estado.color} sx={{ mt: 1 }}>
              {estado.label}
            </Chip>
          </Box>

          <Divider />

          {/* Los teléfonos: lo único que identifica a las partes */}
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <Dato
              etiqueta="Cliente"
              valor={
                cliente ? (
                  <>
                    {cliente}{' '}
                    <Chip size="sm" variant="soft" color="success">
                      contacto enlazado
                    </Chip>
                  </>
                ) : (
                  'No registrado en ChatEAM'
                )
              }
            />
            <Dato
              etiqueta="Teléfono del cliente"
              valor={telCliente || 'No disponible'}
              mono
            />
            <Dato
              etiqueta="Teléfono del negocio"
              valor={telNegocio || 'No disponible'}
              mono
            />
          </Stack>

          {telCliente && (
            <Button
              size="sm"
              variant="soft"
              component="a"
              href={`https://wa.me/${telCliente}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ alignSelf: 'flex-start' }}
            >
              Abrir chat con el cliente
            </Button>
          )}

          <Divider />

          {/* Estado del aviso al grupo: explica por qué llegó o no */}
          <Box>
            <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.5 }}>
              Aviso al grupo interno
            </Typography>
            {meta.avisoEnviado ? (
              <Alert color="success" variant="soft" size="sm">
                Enviado al grupo correspondiente
              </Alert>
            ) : (
              <Alert color="warning" variant="soft" size="sm">
                {meta.avisoMotivo === 'no_group'
                  ? 'No se envió: no hay ningún grupo configurado para esta conexión ni grupo por defecto.'
                  : meta.avisoMotivo === 'connection_not_connected'
                  ? 'No se envió: la conexión de WhatsApp del grupo está desconectada.'
                  : meta.avisoMotivo === 'cita_antigua'
                  ? 'No se envió: la cita es anterior al periodo de avisos.'
                  : meta.avisoMotivo === 'channel_not_supported'
                  ? 'No se envió: la conexión es Cloud API y Meta no soporta grupos.'
                  : `No se envió (${meta.avisoMotivo || 'motivo desconocido'}).`}
              </Alert>
            )}
          </Box>

          <Box>
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              Identificador del evento en Google
            </Typography>
            <Typography
              level="body-xs"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              {appointment.googleCalendarEventId || '—'}
            </Typography>
          </Box>

          <Alert color="neutral" variant="soft" size="sm">
            Para reagendar o cancelar esta cita hay que hacerlo{' '}
            <b>en Google Calendar</b>. ChatEAM recogerá el cambio en la siguiente
            sincronización.
          </Alert>

          <Button onClick={onClose} sx={{ alignSelf: 'flex-end' }}>
            Cerrar
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  )
}
